/**
 * Cookie consent preference (GDPR/ePrivacy).
 * - essential: auth/session/prefs only — no analytics
 * - all: essential + PostHog analytics
 */

export type CookieConsent = "essential" | "all";

export const COOKIE_CONSENT_KEY = "troopn_cookie_consent";
/** Legacy banner key — Essential and Accept All both wrote "true" (non-compliant). */
const LEGACY_CONSENT_KEY = "troopn_consent_accepted";

export const COOKIE_CONSENT_CHANGED_EVENT = "troopn:cookie-consent-changed";

export function getCookieConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (raw === "essential" || raw === "all") return raw;
    // Force re-choice after broken banner (legacy true meant both buttons)
    if (localStorage.getItem(LEGACY_CONSENT_KEY) !== null) {
      localStorage.removeItem(LEGACY_CONSENT_KEY);
    }
    return null;
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent(): boolean {
  return getCookieConsent() === "all";
}

export function setCookieConsent(value: CookieConsent): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, value);
    localStorage.removeItem(LEGACY_CONSENT_KEY);
  } catch {
    // private mode / quota — still dispatch so UI can proceed
  }
  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT, { detail: { consent: value } }),
  );
}

export function clearCookieConsent(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    localStorage.removeItem(LEGACY_CONSENT_KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT, { detail: { consent: null } }),
  );
}
